import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_SERVICE_CARDS
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const toNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Map Appwrite document → maintenance/service-card row used by Fleet Maintenance. */
export const formatServiceCard = (doc) => ({
  id: doc.$id ?? doc.id,
  vehicleId: doc.vehicleId || '',
  plate: doc.plate || '',
  driver: doc.driver || '',
  project: doc.project || '',
  date: doc.serviceDate || '',
  serviceDate: doc.serviceDate || '',
  servicedOdometer: toNumber(doc.servicedMileage, 0),
  servicedMileage: toNumber(doc.servicedMileage, 0),
  nextServiceKm: toNumber(doc.nextServiceKm, null),
  notes: doc.notes || '',
  item: doc.item || 'Service',
  category: 'Service',
  supplier: '—',
  cost: 0,
  invoiceNumber: '—',
  attachment: null,
  status: doc.status || 'completed',
  submittedByEmail: doc.submittedByEmail || '',
  submittedByName: doc.submittedByName || ''
});

const toPayload = (fields) => ({
  vehicleId: String(fields.vehicleId || '').trim(),
  plate: String(fields.plate || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, ''),
  driver: String(fields.driver || '').trim(),
  project: String(fields.project || '').trim(),
  serviceDate: String(fields.serviceDate || fields.date || '').trim(),
  servicedMileage: toNumber(fields.servicedMileage ?? fields.servicedOdometer, 0) ?? 0,
  nextServiceKm: toNumber(fields.nextServiceKm, 0) ?? 0,
  notes: String(fields.notes || '').trim(),
  item: String(fields.item || 'Service').trim(),
  status: String(fields.status || 'completed').trim(),
  submittedByEmail: String(fields.submittedByEmail || '').trim().toLowerCase(),
  submittedByName: String(fields.submittedByName || '').trim()
});

export const getServiceCardErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

const runList = async (extraQueries) => {
  const all = [];
  let cursor = null;
  const limit = 100;

  for (let page = 0; page < 20; page += 1) {
    const queries = [Query.limit(limit), Query.orderDesc('serviceDate'), ...extraQueries];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_SERVICE_CARDS,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatServiceCard));
    if (docs.length < limit) break;
    cursor = docs[docs.length - 1].$id;
  }
  return all;
};

/** Fetch service cards for a vehicle id and/or plate. */
export const fetchServiceCardsForVehicle = async ({ vehicleId, plate } = {}) => {
  const all = [];
  const id = String(vehicleId || '').trim();
  const plateNorm = String(plate || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, '');

  if (id) {
    all.push(...(await runList([Query.equal('vehicleId', id)])));
  }
  if (plateNorm) {
    const byPlate = await runList([Query.equal('plate', plateNorm)]);
    const seen = new Set(all.map((r) => r.id));
    byPlate.forEach((row) => {
      if (!seen.has(row.id)) all.push(row);
    });
  }

  return all.sort((a, b) => String(b.date).localeCompare(String(a.date)));
};

/** Create a new service card record. */
export const createServiceCard = async (fields) => {
  const payload = toPayload(fields);
  if (!payload.vehicleId || !payload.serviceDate) {
    throw new Error('vehicleId and serviceDate are required.');
  }
  if (!payload.servicedMileage || payload.servicedMileage <= 0) {
    throw new Error('servicedMileage must be greater than 0.');
  }
  if (!payload.nextServiceKm || payload.nextServiceKm <= 0) {
    throw new Error('nextServiceKm must be greater than 0.');
  }

  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_SERVICE_CARDS,
    ID.unique(),
    payload
  );
  return formatServiceCard(created);
};
